import React from 'react'
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Hidden,
  Button,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Link as RouterLink } from 'react-router-dom'
import { isUndefined } from 'lodash'

import ArrowBackIcon from 'components/material-symbol/icons/ArrowBack'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import LinearProgress from '@material-ui/core/LinearProgress'
import { PageHeaderTabsContext } from './page-header-tabs-section'
import {
  BACK_BUTTON_SLOT_WIDTH,
  pageHeaderActionsColumnIndex,
  pageHeaderGridColumnsWithActions,
  pageHeaderHorizontalPadding,
  pageHeaderLabelColumn,
  pageHeaderLabelColumnIndex,
  pageHeaderBackButton,
} from './page-header-layout'
import { pageTitleCapitalization } from 'lib/typography/capitalization-styles'

export { PageHeaderTabsContext, pageHeaderHorizontalPadding }

const getBackButtonTarget = (crumbs, backTo) => {
  if (backTo) return backTo
  if (!crumbs?.length) return null

  for (let index = crumbs.length - 1; index >= 0; index -= 1) {
    if (crumbs[index]?.to) return crumbs[index].to
  }

  return null
}

const useStyles = makeStyles(theme => ({
  pageHeaderShell: {
    width: '100%',
  },
  pageHeaderInset: {
    ...pageHeaderHorizontalPadding(theme),
  },
  pageHeaderGrid: ({ hasCrumbs, hasTabs }) => ({
    display: 'grid',
    gridTemplateColumns: pageHeaderGridColumnsWithActions(hasCrumbs),
    gridTemplateRows: hasTabs ? 'auto auto auto' : 'auto',
    alignItems: 'start',
    borderTopColor: theme.palette.grey[300],
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    paddingTop: theme.spacing(4),
    paddingBottom: hasTabs ? 0 : theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: hasCrumbs
        ? '48px minmax(0, 1fr) auto'
        : 'minmax(0, 1fr) auto',
      paddingTop: theme.spacing(2),
      paddingBottom: hasTabs ? 0 : theme.spacing(2),
    },
  }),
  tabsDividerCell: {
    gridColumn: '1 / -1',
    gridRow: 3,
    width: '100%',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  backButtonCell: ({ hasTabs }) => ({
    gridColumn: 1,
    gridRow: hasTabs ? '1 / span 2' : 1,
    alignSelf: 'start',
  }),
  labelColumn: ({ hasCrumbs }) => ({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    ...pageHeaderLabelColumn(theme, hasCrumbs),
  }),
  titleCell: ({ hasCrumbs }) => ({
    gridColumn: pageHeaderLabelColumnIndex(hasCrumbs),
    gridRow: 1,
  }),
  tabsCell: ({ hasCrumbs }) => ({
    gridColumn: pageHeaderLabelColumnIndex(hasCrumbs),
    gridRow: 2,
    marginTop: theme.spacing(3),
    width: '100%',
    minWidth: 0,
  }),
  actionsCell: ({ hasCrumbs }) => ({
    gridColumn: pageHeaderActionsColumnIndex(hasCrumbs),
    gridRow: 1,
    alignSelf: 'center',
  }),
  title: {
    margin: 0,
    fontWeight: '500',
    fontSize: '30px',
    lineHeight: '42px',
    ...pageTitleCapitalization,
    [theme.breakpoints.down('xs')]: {
      fontSize: '22px',
      lineHeight: '28px',
    },
  },
  profileTitle: {
    color: '#172024',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
    width: '100%',
  },
  avatarSlot: {
    flexShrink: 0,
  },
  titleTextColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
  },
  subtitle: {
    color: '#6B7C87',
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: theme.spacing(0.5),
  },
  crumbTitle: {
    ...pageTitleCapitalization,
  },
  crumbSeparator: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 16,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  backButtonRoot: {
    ...pageHeaderBackButton(theme),
  },
  backButtonIcon: {
    fontSize: 24,
  },
  backButtonSlot: {
    width: BACK_BUTTON_SLOT_WIDTH,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('xs')]: {
      width: 48,
    },
  },
  actionWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& > *': {
      marginRight: theme.spacing(2),
      marginBottom: 0,
    },
    '& > *:last-child': {
      marginRight: 0,
    },
  },
  actionWrapperStacked: {
    [theme.breakpoints.only('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'flex-end',

      '& > *': {
        marginBottom: theme.spacing(2),
        marginRight: 0,
      },

      '& > *:first-child': {
        marginBottom: 0,
      },
    },
  },
  actionWrapperInline: {
    [theme.breakpoints.only('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      gap: theme.spacing(1.5),

      '& > *': {
        marginRight: 0,
        marginBottom: 0,
      },
    },
  },
  mobileActionWrapper: {
    [theme.breakpoints.only('xs')]: {
      flexDirection: 'row',
      zIndex: theme.zIndex.appBar,
      width: '100%',
      position: 'fixed',
      bottom: 0,
      left: 0,
      background: '#fff',
      padding: theme.spacing(2, 2),
      minHeight: 70,
      justifyContent: 'center',
      gap: theme.spacing(1.5),
      boxSizing: 'border-box',
      boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.2)',
      '& > *': {
        marginRight: 0,
        marginBottom: 0,
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
      },
    },
  },
  action: {
    color: 'rgba(0, 0, 0, 0.54)',
  },
  progress: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    color: '#3268A7',
    marginBottom: '30px',
    paddingLeft: '30px',
    paddingRight: '30px',
  },
  progressBar: {
    width: 'calc(100% - 60px)',
    height: '12px',
    borderRadius: '4px',
    backgroundColor: 'rgba(158, 171, 186, 0.6)',
  },
  bar: {
    backgroundColor: '#3268A7',
  },
}))

function PageCrumbs({ title, crumbs }) {
  const classes = useStyles()
  if (!crumbs) {
    return null
  }

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      separator={<ChevronRightIcon className={classes.crumbSeparator} />}
    >
      {crumbs.map((value, index) => {
        return value.to ? (
          <Link
            component={RouterLink}
            color="inherit"
            key={index}
            to={value.to}
          >
            {value.label}
          </Link>
        ) : (
          <Typography
            color="textPrimary"
            className={classes.crumbTitle}
            key={index}
          >
            {value.label}
          </Typography>
        )
      })}
      {crumbs.length < 2 ? (
        <Typography color="textPrimary" className={classes.crumbTitle}>
          {title}
        </Typography>
      ) : null}
    </Breadcrumbs>
  )
}

export default function PageHeader({
  title,
  crumbs = [],
  actions,
  tabs,
  fixedMobileActions,
  keepActionsInline = false,
  mobileCornerAction,
  progress,
  avatar,
  subtitle,
  hideCrumbs = false,
  titleClassName,
  gridClassName,
  backTo,
  ...props
}) {
  const hasCrumbs = crumbs && crumbs.length > 0
  const hasTabs = Boolean(tabs)
  const showBackButton = hasCrumbs || Boolean(backTo)
  const backButtonTarget = getBackButtonTarget(crumbs, backTo)
  const classes = useStyles({ hasCrumbs: showBackButton, hasTabs })

  return (
    <Box className={classes.pageHeaderShell}>
      <Box {...props}>
        <Box className={classes.pageHeaderInset}>
          <Box className={`${classes.pageHeaderGrid} ${gridClassName || ''}`}>
            {showBackButton && backButtonTarget ? (
              <Box
                className={`${classes.backButtonSlot} ${classes.backButtonCell}`}
              >
                <Button
                  component={RouterLink}
                  to={backButtonTarget}
                  className={classes.backButtonRoot}
                >
                  <ArrowBackIcon className={classes.backButtonIcon} />
                </Button>
              </Box>
            ) : null}
            <Box className={`${classes.labelColumn} ${classes.titleCell}`}>
              {hasCrumbs && !hideCrumbs ? (
                <Hidden smDown>
                  <PageCrumbs {...{ crumbs, title }} />
                </Hidden>
              ) : null}
              <Box className={classes.titleRow}>
                {avatar ? (
                  <Box className={classes.avatarSlot}>{avatar}</Box>
                ) : null}
                <Box className={classes.titleTextColumn}>
                  <Typography
                    variant="h2"
                    color="textPrimary"
                    className={`${classes.title} ${titleClassName || ''} ${
                      avatar ? classes.profileTitle : ''
                    }`}
                    data-testid="page-header-title"
                  >
                    {title}
                  </Typography>
                  {subtitle ? (
                    <Typography
                      variant="body2"
                      className={classes.subtitle}
                      data-testid="page-header-subtitle"
                    >
                      {subtitle}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>
            {actions || mobileCornerAction ? (
              <Box
                className={`${classes.actionWrapper} ${
                  keepActionsInline
                    ? classes.actionWrapperInline
                    : classes.actionWrapperStacked
                } ${classes.actionsCell} ${
                  fixedMobileActions ? classes.mobileActionWrapper : ''
                }`}
              >
                <Hidden smUp>{mobileCornerAction || null}</Hidden>
                {actions}
              </Box>
            ) : null}
            {hasTabs ? (
              <Box className={classes.tabsCell}>
                <PageHeaderTabsContext.Provider value>
                  {tabs}
                </PageHeaderTabsContext.Provider>
              </Box>
            ) : null}
            {hasTabs ? (
              <Box className={classes.tabsDividerCell} aria-hidden />
            ) : null}
          </Box>
          {!isUndefined(progress) && (
            <Box className={classes.progress}>
              <LinearProgress
                color="primary"
                variant="determinate"
                value={progress}
                classes={{
                  root: classes.progressBar,
                  barColorPrimary: classes.bar,
                }}
              />
              <Typography variant="h6">{progress} %</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
